import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, X, Check, Loader2 } from "lucide-react";
import { Reveal, Tag, Heading } from "./Reveal";
import { CTA } from "./CTA";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { trackLead } from "../lib/pixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_OPTIONS = ["Phone Call", "Text Message", "Email"];
const WORK_OPTIONS = ["Backsplash & Tile", "Countertops", "Flooring", "Lighting", "Bathroom", "Repairs"];

const inputCls = "rounded-none border-0 border-b border-ink/25 bg-transparent h-12 px-0 text-base text-ink shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-ink placeholder:text-ink/30 transition-colors";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.16em] text-ink/55";

const Chip = ({ active, onClick, testId, children, activeCls = "bg-ink text-bone border-ink" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`font-sans font-medium text-sm px-4 py-2.5 border transition-colors duration-300 ${active ? activeCls : "border-ink/20 text-ink/70 hover:border-ink hover:text-ink"}`}
  >
    {children}
  </button>
);

export const QuoteForm = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", preferred_contact: "Phone Call", square_footage: "", cabinet_count: "", project_description: "" });
  const [otherWork, setOtherWork] = useState([]);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInput = useRef(null);
  const leadFired = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleWork = (w) => setOtherWork((list) => (list.includes(w) ? list.filter((x) => x !== w) : [...list, w]));

  const addFiles = (incoming) => {
    const imgs = Array.from(incoming).filter((f) => f.type.startsWith("image/"));
    setFiles((prev) => [...prev, ...imgs].slice(0, 6));
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please add your name, email and phone number.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("other_work", otherWork.join(","));
      files.forEach((f) => fd.append("photos", f));
      await axios.post(`${API}/quotes`, fd);
      if (!leadFired.current) {
        leadFired.current = true;
        trackLead("kitchen_quote");
      }
      setSubmitted(true);
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote-form" className="py-24 lg:py-40 border-t border-ink/10" data-testid="quote-form-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
          <Tag>Ready for the kitchen glow-up?</Tag>
            <Heading className="mt-6">
              Your new kitchen for <span className="text-clay">$5K.</span> Guaranteed.
            </Heading>
            <p className="mt-8 text-base sm:text-lg text-ink/70 leading-relaxed max-w-md">
              Send us a few details about your kitchen. Photos are even better. Up to 18 cabinets plus granite countertops is a guaranteed $5,000 — and if you want to go further, we'll walk you through exactly what that costs.
            </p>
            <p className="mt-6 font-display font-medium text-xl sm:text-2xl tracking-[-0.015em] text-ink max-w-md">
              Your kitchen could look completely different a few days from now.*
            </p>
            <p className="mt-10 text-xs text-ink/45 leading-relaxed max-w-md" data-testid="footnote-disclaimer">
              *The $5,000 guaranteed price covers new cabinets (up to 18) and granite countertops. Kitchens with more than 18 cabinets, additional work (backsplash, flooring, lighting, etc.) and unusual site conditions are quoted separately and clearly before any work begins. Installation time varies by project.
            </p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          {submitted ? (
            <div className="min-h-[420px] flex flex-col justify-center border border-ink/15 p-10 sm:p-14" data-testid="quote-form-success">
              <span className="h-12 w-12 bg-teal text-bone flex items-center justify-center"><Check className="h-6 w-6" strokeWidth={3} /></span>
              <h3 className="mt-8 font-display font-semibold text-4xl sm:text-5xl tracking-[-0.02em] text-ink">Thank you — we've got it.</h3>
              <p className="mt-5 text-base sm:text-lg text-ink/70 max-w-md leading-relaxed">
                We'll review your kitchen details and reach out via your preferred contact method to talk through what's possible.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <Label htmlFor="qf-name" className={labelCls}>Full name *</Label>
                  <Input id="qf-name" data-testid="quote-form-name-input" className={`mt-1 ${inputCls}`} placeholder="Jane Doe" value={form.full_name} onChange={set("full_name")} required />
                </div>
                <div>
                  <Label htmlFor="qf-phone" className={labelCls}>Phone number *</Label>
                  <Input id="qf-phone" type="tel" data-testid="quote-form-phone-input" className={`mt-1 ${inputCls}`} placeholder="(555) 234-5678" value={form.phone} onChange={set("phone")} required />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="qf-email" className={labelCls}>Email address *</Label>
                  <Input id="qf-email" type="email" data-testid="quote-form-email-input" className={`mt-1 ${inputCls}`} placeholder="jane@example.com" value={form.email} onChange={set("email")} required />
                </div>
                <div className="sm:col-span-2" data-testid="quote-form-contact-pref">
                  <Label className={labelCls}>Preferred method of contact</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {CONTACT_OPTIONS.map((opt) => (
                      <Chip key={opt} active={form.preferred_contact === opt} onClick={() => setForm((f) => ({ ...f, preferred_contact: opt }))} testId={`contact-pref-${opt.toLowerCase().replace(/\s+/g, "-")}`}>
                        {opt}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="qf-sqft" className={labelCls}>Kitchen square footage (approx)</Label>
                  <Input id="qf-sqft" data-testid="quote-form-sqft-input" className={`mt-1 ${inputCls}`} placeholder="e.g. 180 sq ft" value={form.square_footage} onChange={set("square_footage")} />
                </div>
                <div>
                  <Label htmlFor="qf-cabinets" className={labelCls}>Cabinets you want replaced</Label>
                  <Input id="qf-cabinets" data-testid="quote-form-cabinets-input" className={`mt-1 ${inputCls}`} placeholder="e.g. 12 uppers, 10 lowers — estimate is fine" value={form.cabinet_count} onChange={set("cabinet_count")} />
                </div>
                <div className="sm:col-span-2" data-testid="quote-form-other-work">
                  <Label className={labelCls}>Any other work you want done?</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {WORK_OPTIONS.map((opt) => (
                      <Chip key={opt} active={otherWork.includes(opt)} onClick={() => toggleWork(opt)} testId={`other-work-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`} activeCls="bg-teal text-bone border-teal">
                        {opt}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="qf-desc" className={labelCls}>Tell us about your project</Label>
                  <Textarea id="qf-desc" data-testid="quote-form-description-input" className="mt-1 rounded-none border-0 border-b border-ink/25 bg-transparent min-h-[110px] px-0 py-3 text-base text-ink shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-ink placeholder:text-ink/30" placeholder="Describe your kitchen, what you'd love to change, colors, timeline..." value={form.project_description} onChange={set("project_description")} />
                </div>
                <div className="sm:col-span-2">
                  <Label className={labelCls}>Photos of your current kitchen (optional)</Label>
                  <div
                    onClick={() => fileInput.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                    data-testid="quote-form-file-upload"
                    className="mt-3 cursor-pointer border border-dashed border-ink/30 hover:border-ink hover:bg-sand transition-colors duration-300 px-6 py-10 flex flex-col items-center text-center"
                  >
                    <ImagePlus className="h-6 w-6 text-ink/50" />
                    <p className="mt-3 text-sm text-ink/70">Drag & drop photos here, or <span className="text-ink font-semibold underline underline-offset-4">browse</span></p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-ink/40">Up to 6 images</p>
                    <input ref={fileInput} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                  </div>
                  {files.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="quote-form-file-previews">
                      {files.map((f, i) => (
                        <div key={`${f.name}-${i}`} className="relative h-20 w-20 overflow-hidden">
                          <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                          <button type="button" onClick={() => removeFile(i)} aria-label="Remove photo" className="absolute top-0 right-0 h-6 w-6 bg-ink text-bone flex items-center justify-center hover:bg-clay">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <CTA type="submit" disabled={sending} testId="quote-form-submit-button" className="mt-12 w-full !py-6 text-base sm:text-lg">
                {sending ? <span className="inline-flex items-center gap-3"><Loader2 className="h-4 w-4 animate-spin" /> Sending...</span> : "Get my free kitchen quote"}
              </CTA>
              <p className="mt-4 text-sm text-ink/50">No pressure, no obligation — just a real number for your real kitchen.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
